import React, { useRef, useEffect, useState } from 'react'
import { appointmentDateFormat } from '../../../utils/dateUtils'
import StatusChange from './StatusChange'
import { useAppSelector } from '../../../app/hooks'
import { AppointmentData, selectProvenanceKey } from '../appointmentsSlice'
import VisitLink from './VisitLink'
import MeetingLink from './MeetingLink'
import MaskedNumber from '../../../components/MaskedNumber'
import KeycloakService from '../../../utils/keycloakService'
import { MEETING_LINK } from '../../../utils/roles'
import { selectLocations } from '../../location/locationSlice'
import BillingLink from './BillingLink'
import { startSxpProxy } from '../../../utils/api'
import { CHAT_PROJECT_ID, pageSizeOptions } from '../../../utils/constants'
import { Practitioner, Provenance } from 'fhir/r4'
import { getProvenanceData } from '../../patients/utils'
import { ProvenanceData } from '../../patients/patientsSlice'
import ProvenanceView from '../../../components/ProvenanceView'
import {
  cancelled,
  checkedIn,
  fetchAppointments,
  fulfilled,
  paid,
} from '../models'
import CustomHeader from '../../Radiology/components/RadiologyModel/CustomHeader'
import { ColDef } from 'ag-grid-community'
import { Box, useTheme } from '@mui/material'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import MedAgDataGrid from '../../../components/MedAgDataGrid'
import { create, update } from '../../lms/constants'

type Props = {
  appointmentsData: any[]
  handleChange: (status: string, appointment: AppointmentData) => void
  page: number
  pageSize: number
  totalCount: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  loading: boolean
  onSelect: (e: string | number | null) => void
  reload: number
}
const styles = () => ({
  patientheading: {
    color: '#090968',
    fontSize: '16px',
    fontWeight: 600,
  },
  deviceText: {
    color: '#090968',
    fontSize: '16px',
    fontWeight: 600,
  },
  centerHeader: {
    color: '#ffffff',
    textAlign: 'center',
    '& .ag-header-cell-text': {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
    },
  },
})

const AppointmentsTable = ({
  appointmentsData,
  handleChange,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  loading,
  onSelect,
  reload,
}: Props) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const provenanceKey = useAppSelector(selectProvenanceKey)
  const locations = useAppSelector(selectLocations)
  const locationName = (selectedId: any) => {
    const selectedLocation = locations?.find(
      (e: any) => e.resource?.id === selectedId
    )
    return selectedLocation?.resource?.name ?? '-'
  }
  const [provenances, setProvenances] = useState<
    Record<string, ProvenanceData>
  >({})

  const ids = appointmentsData?.map((f) => f?.id)?.join(',')
  const [selectedPatientId, setSelectedPatientId] = useState<
    string | number | null
  >(null)
  useEffect(() => {
    if (ids) {
      startSxpProxy(CHAT_PROJECT_ID, fetchAppointments, {
        appointmentIds: ids,
      })
        ?.then((data) => {
          const entries =
            data?.data?.entry?.map(
              (dde: { resource: Provenance | Practitioner }) => dde?.resource
            ) ?? []
          const obj = getProvenanceData(entries)
          setProvenances(obj)
        })
        .catch((err) => console.log(err))
    }
  }, [ids, provenanceKey])
  useEffect(() => {
    setSelectedPatientId(null)
  }, [reload])

  const appointmentsTableContainerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const resizeHandler = () => {
      const container = appointmentsTableContainerRef.current
      if (container) {
        const availableHeight: any =
          window.innerHeight - container.getBoundingClientRect().top
        container.style.maxHeight = `${availableHeight - 35}px`
      }
    }

    window.addEventListener('resize', resizeHandler)
    resizeHandler()

    return () => {
      window.removeEventListener('resize', resizeHandler)
    }
  }, [])
  const renderName = (params: any) => {
    const d = params?.data
    return (
      <>
        {d.patientName}
        {d.status !== cancelled && <VisitLink appointment={d} />}
      </>
    )
  }
  const renderConsultant = (params: any) => {
    return params?.data?.practitioner
      ? `Dr. ${params?.data?.practitioner}`
      : 'NA'
  }
  const renderSlot = (params: any) => {
    const d = params?.data
    return (
      <>
        {appointmentDateFormat(params?.data?.slotTiming)}
        {d.meetingLink &&
        d.status === checkedIn &&
        KeycloakService.hasRole([MEETING_LINK]) ? (
          <MeetingLink appointment={d} />
        ) : null}
      </>
    )
  }
  const columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'checkbox',
      width: 50,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { textAlign: 'center' },
      checkboxSelection: false, // AG Grid built-in selection is off
      cellRenderer: (params: any) => {
        const d = params?.data
        return (
          <input
            type='checkbox'
            width={'10px'}
            height={'10px'}
            className='checkbox-input'
            checked={selectedPatientId === d?.id}
            onChange={() => {
              setSelectedPatientId(selectedPatientId === d?.id ? null : d?.id)
              onSelect(selectedPatientId === d?.id ? null : d?.id)
            }}
          />
        )
      },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'SL No',
      field: 'slNo',
      width: 80,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => params?.node?.rowIndex + 1,
      headerClass: 'custom-header-cell',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'UHID',
      field: 'name',
      flex: 1,
      minWidth: 140,
      sortable: false,
      filter: false,
      headerComponent: CustomHeader,
      cellRenderer: (params: any) =>
        params?.data?.patientUhid || params?.data?.patientThopId,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'left' },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Name',
      field: 'name',
      minWidth: 300,
      sortable: true,
      filter: true,
      cellRenderer: renderName,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1,
        display: 'flex',
      },
      wrapText: true,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Mobile',
      field: 'mobile',
      flex: 1,
      minWidth: 120,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => (
        <MaskedNumber mobile={params?.data?.patientNumber} />
      ),
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Operating Unit',
      field: 'operatingUnit',
      flex: 1,
      minWidth: 140,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        return <>{locationName(params?.data?.locationId)}</>
      },
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Specialty',
      field: 'name',
      minWidth: 135,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => params?.data?.speciality,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: classes?.centerHeader,
      cellStyle: { fontSize: '12px', textAlign: 'left' },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Consultant',
      field: 'name',
      flex: 1,
      minWidth: 200,
      sortable: true,
      filter: true,
      cellRenderer: renderConsultant,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        display: 'flex',
        alignItems: 'center',
        lineHeight: 1,
      },
      wrapText: true,
    },
    {
      headerName: 'Slot Timing',
      field: 'name',
      minWidth: 145,
      sortable: false,
      filter: false,
      cellRenderer: renderSlot,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'center',
      },
      wrapText: true,
    },
    {
      headerName: 'Type',
      field: 'name',
      minWidth: 80,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => params?.data?.consultationType ?? '-',
      cellClass: 'custom-header-cell',
      headerClass: classes?.centerHeader,
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Status',
      field: 'status',
      flex: 1,
      minWidth: 120,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const d = params?.data
        const shouldDisable = (d: AppointmentData): boolean => {
          return d.status === fulfilled || d.status === cancelled
        }
        return (
          <div className='flex apart'>
            <StatusChange
              initialStatus={d?.status}
              disabled={shouldDisable(d)}
              handleChange={(e) => handleChange(e, d)}
              appointmentDate={d?.slotTiming}
            />
          </div>
        )
      },
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
      },
    },
    {
      headerName: 'Pay.Status',
      field: 'name',
      minWidth: 100,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => {
        return (
          <>
            {params?.data?.consultationType === paid &&
            params?.data?.status !== cancelled ? (
              <BillingLink
                status={params?.data?.status}
                thopId={params?.data?.thopId}
              />
            ) : (
              '-'
            )}
          </>
        )
      },
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Created By',
      field: 'createdBy',
      flex: 1,
      minWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const d = params?.data
        return (
          <ProvenanceView
            date={provenances[d.id]?.createdOn}
            name={provenances[d.id]?.createdBy}
            mode={create}
          />
        )
      },
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.5,
      },
      wrapText: true,
    },
    {
      headerName: 'Updated By',
      field: 'name',
      flex: 1,
      minWidth: 130,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => {
        const d = params?.data
        return (
          <ProvenanceView
            date={provenances[d.id]?.updatedOn ?? provenances[d.id]?.createdOn}
            name={provenances[d.id]?.updatedBy ?? provenances[d.id]?.createdBy}
            mode={update}
          />
        )
      },
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.5,
      },
      wrapText: true,
    },
  ]
  return (
    <Box height={'93%'} width={'100%'}>
      <MedAgDataGrid
        tableHeight={'100%'}
        rowData={loading ? [] : appointmentsData}
        columnDefs={columnDefs}
        pagination={true}
        exportIcon={false}
        searchField={false}
        headerHeight={30}
        rowHeight={40}
        localSearch={false}
        enableCellTextSelection={true}
        pageSizeOptions={pageSizeOptions}
        page={page}
        setPage={onPageChange}
        pageSize={pageSize}
        setPageSize={onPageSizeChange}
        totalRows={totalCount}
      />
    </Box>
  )
}

export default AppointmentsTable
